import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { Logo } from '../layout/Logo';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { spring } from '../../utils/motion';
export interface Step {
  id: string;
  label: string;
  title: string;
  blurb: string;
}
export function OnboardingFrame({
  steps,
  index,
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  exitTo,
  aside,
  children
}: {
  steps: Step[];
  index: number;
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  exitTo: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  const step = steps[index];
  const progress = (index + 1) / steps.length * 100;
  return <div className="flex min-h-screen w-full flex-col bg-canvas" data-id="element-896" data-mp="3ep4dnvsr7s5l">
      <header className="border-b border-hairline bg-surface" data-id="element-897" data-mp="3eqz2hl75c4ey">
        <div className="mx-auto flex max-w-shell items-center gap-4 px-5 py-3.5 lg:px-8" data-id="element-898" data-mp="2sg01rn3ndouh">
          <Logo data-id="element-899" data-mp="2xd0ak5rvt5pa" />
          <span className="tnum ml-auto text-[13px] text-ink-3" data-id="element-900" data-mp="2xdjhkrfzu9oj">
            Step {index + 1} of {steps.length}
          </span>
          <Link to={exitTo} className="text-[13px] font-medium text-ink-2 underline underline-offset-2 hover:text-ink" data-id="element-901" data-mp="2ij575fahw8lh">
            Finish later
          </Link>
        </div>
        <div className="h-[3px] w-full bg-oat" aria-hidden="true" data-id="element-902" data-mp="29mwa279zpm2i">
          <motion.div className="h-full bg-primary" initial={false} animate={{
          width: `${progress}%`
        }} transition={reduce ? {
          duration: 0.15
        } : spring.smooth} data-id="element-903" data-mp="2effjqt5k3x21" />
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-shell flex-1 gap-10 px-5 py-10 lg:grid-cols-[200px_minmax(0,1fr)_260px] lg:px-8 lg:py-14" data-id="element-904" data-mp="24n9vnuifdzs4">
        <nav aria-label="Progress" className="hidden lg:block" data-id="element-905" data-mp="1urpzr83v313d">
          <ol className="relative space-y-4" data-id="element-906" data-mp="1ut14pb91mew2">
            <span className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-hairline" aria-hidden="true" data-id="element-907" data-mp="19j04164rihp" />
            {steps.map((s, i) => {
            const done = i < index;
            const current = i === index;
            return <li key={s.id} className="flex items-center gap-3" data-id="element-908" data-mp="3nlppx1uudp25" data-mpi={i}>
                  <span className={cn('relative z-10 flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-semibold', done && 'border-primary bg-primary text-on-primary', current && 'border-primary bg-surface text-primary', !done && !current && 'border-hairline bg-surface text-ink-3')} aria-hidden="true" data-id="element-909" data-mp="3iqjzpquepo89">
                    {done ? <CheckIcon className="h-3 w-3" data-id="element-910" data-mp="2qxt33f8t9xih" /> : i + 1}
                  </span>
                  <span className={cn('text-[13px]', current ? 'font-semibold text-ink' : done ? 'text-ink-2' : 'text-ink-3')} data-id="element-911" data-mp="2qzvzqi4gyow6">
                    {s.label}
                  </span>
                </li>;
          })}
          </ol>
        </nav>

        <main className="min-w-0" data-id="element-912" data-mp="1ffdy80c64il1">
          <AnimatePresence mode="wait" data-id="element-913" data-mp="1fecdf45cohq4">
            <motion.div key={step.id} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            x: 18
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            x: -14
          }} transition={reduce ? {
            duration: 0.16
          } : spring.smooth} data-id="element-914" data-mp="1k7oxd0z340cj">
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-action" data-id="element-915" data-mp="1y884m5vq72dn">{step.label}</p>
              <h1 className="mt-2 max-w-[26ch] font-display text-[clamp(1.8rem,3.4vw,2.6rem)] font-semibold leading-[1.08] tracking-[-0.025em] text-ink" data-id="element-916" data-mp="235hu1lj47e1h">
                {step.title}
              </h1>
              <p className="mt-3 max-w-[58ch] text-base leading-relaxed text-ink-2" data-id="element-917" data-mp="8siettincluq">{step.blurb}</p>
              <div className="mt-8" data-id="element-918" data-mp="39ld6tnzbrlvk">{children}</div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-hairline pt-6" data-id="element-919" data-mp="34nv8ssmuir2c">
            <Button type="button" variant="action" size="lg" onClick={onNext} disabled={nextDisabled} data-id="element-920" data-mp="3jkm8l30fm4sh">
              {nextLabel}
            </Button>
            {index > 0 && <Button type="button" variant="ghost" size="lg" onClick={onBack} data-id="element-921" data-mp="2qxi0uq9yfcd0">
                Back
              </Button>}
          </div>
        </main>

        {aside && <aside className="lg:pt-16" data-id="element-922" data-mp="30mrqlzhbsixy">{aside}</aside>}
      </div>
    </div>;
}
export function OnboardingDone({
  title,
  body,
  primary,
  secondary
}: {
  title: string;
  body: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  return <div className="flex min-h-screen w-full items-center justify-center bg-canvas px-5 py-16" data-id="element-923" data-mp="237xcfrv17mwc">
      <motion.div initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 16,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={reduce ? {
      duration: 0.2
    } : spring.heavy} className="w-full max-w-lg text-center" data-id="element-924" data-mp="iotk6rn3uv9c">
        <motion.span initial={reduce ? {
        opacity: 0
      } : {
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={reduce ? {
        duration: 0.2
      } : {
        type: 'spring',
        stiffness: 380,
        damping: 20,
        delay: 0.12
      }} className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-on-primary" aria-hidden="true" data-id="element-925" data-mp="dq09f6z7dicv">
          <CheckIcon className="h-7 w-7" data-id="element-926" data-mp="wg86b7cb7ckm" />
        </motion.span>
        <h1 className="mt-6 font-display text-[clamp(1.8rem,3.4vw,2.4rem)] font-semibold leading-[1.1] tracking-[-0.025em] text-ink" data-id="element-927" data-mp="weor7k2e37kf">
          {title}
        </h1>
        <p className="mx-auto mt-3 max-w-[46ch] text-base leading-relaxed text-ink-2" data-id="element-928" data-mp="3h7s62yoaaqhe">{body}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-id="element-929" data-mp="1no9tb1b7wnry">
          {primary}
          {secondary}
        </div>
      </motion.div>
    </div>;
}